import React, { useEffect, useState } from 'react';
import { getRobotRequests } from '../../services/api';
import Pager from '../ui/Pager';
import Modal from '../ui/Modal';
import { formatDateTime, badge, td, th, theadRow, muted, label, input } from './userStyles';

const PAGE_SIZE = 20;

const REFUSALS = {
  UNKNOWN_KEY: 'Clé inconnue',
  BAD_TOKEN: 'Token invalide',
  BLOCKED: 'Compte bloqué',
  IP_NOT_ALLOWED: 'IP non autorisée',
};

const AUTH_METHODS = {
  AGENT_KEY: 'Clé',
  JWT: 'JWT',
  LOGIN: 'Login',
  PUBLISH_TOKEN: 'Token publication',
  READ_TOKEN: 'Token lecture',
};

/**
 * Journal d'activité des robots (DO-207) : chaque appel d'API d'un robot (comptes
 * PUSH_AGENT, tokens de service CI), refus compris. Celui d'un robot (`user`) ou le
 * journal global (`user` = null, filtre sur le nom en plus). Survol de l'IP : chaîne
 * X-Forwarded-For brute, utile pour régler TRUSTED_PROXIES / les IP autorisées.
 */
export default function RobotActivityModal({ user, retentionDays, onClose }) {
  const [filters, setFilters] = useState({ username: '', success: '', from: '', to: '' });
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ content: [], totalPages: 0, totalElements: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    getRobotRequests({
      userId: user?.id,
      username: !user && filters.username ? filters.username : undefined,
      success: filters.success || undefined,
      from: filters.from ? filters.from + 'T00:00:00' : undefined,
      to: filters.to ? filters.to + 'T23:59:59' : undefined,
      page,
      size: PAGE_SIZE,
    }).then(setData).finally(() => setLoading(false));
  }, [user, filters, page]);

  const setFilter = field => e => { setFilters({ ...filters, [field]: e.target.value }); setPage(0); };

  return (
    <Modal title={user ? `Activité du robot ${user.username}` : 'Activité des robots'}
           subtitle={`Chaque appel d'API, refus compris — conservé ${retentionDays ?? 30} jours`}
           width={1050} onClose={onClose}>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', margin: '0 0 1rem' }}>
          {!user && (
            <div style={{ flex: '1 1 180px' }}>
              <label style={label}>Robot / token</label>
              <input style={input} value={filters.username} onChange={setFilter('username')} placeholder="contient…" />
            </div>
          )}
          <div style={{ flex: '1 1 150px' }}>
            <label style={label}>Résultat</label>
            <select style={input} value={filters.success} onChange={setFilter('success')}>
              <option value="">Tous</option>
              <option value="true">Réussis</option>
              <option value="false">Refusés / en erreur</option>
            </select>
          </div>
          <div style={{ flex: '1 1 150px' }}>
            <label style={label}>Du</label>
            <input style={input} type="date" value={filters.from} onChange={setFilter('from')} />
          </div>
          <div style={{ flex: '1 1 150px' }}>
            <label style={label}>Au</label>
            <input style={input} type="date" value={filters.to} onChange={setFilter('to')} />
          </div>
        </div>

        <div style={{ overflowX: 'auto', opacity: loading ? 0.6 : 1 }}>
          <table style={{ width: '100%', minWidth: '900px', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={theadRow}>
                {[...(user ? [] : ['Robot']), 'Date', 'Appel', 'Résultat', 'Durée', 'IP', 'Client'].map(h =>
                  <th key={h} style={th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.content.map(r => (
                <tr key={r.id} style={{ borderBottom: '1px solid #ececec' }}>
                  {!user && (
                    <td style={{ ...td, fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {r.username}
                      <div style={{ ...muted, fontStyle: 'normal', fontWeight: 400, fontSize: '0.75rem' }}>
                        {AUTH_METHODS[r.authMethod] || r.authMethod}
                      </div>
                    </td>
                  )}
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{formatDateTime(r.requestedAt)}</td>
                  <td style={{ ...td, fontFamily: 'monospace', fontSize: '0.8rem', maxWidth: '340px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={`${r.httpMethod} ${r.path}`}>
                    <b>{r.httpMethod}</b> {r.path}
                  </td>
                  <td style={td}>
                    {r.status < 400
                      ? <span style={badge('#e2f3e8', '#1a7f37')}>{r.status}</span>
                      : <span style={badge('#fdecea', '#b3261e')}>
                          {r.status}{r.refusalReason ? ` · ${REFUSALS[r.refusalReason] || r.refusalReason}` : ''}
                        </span>}
                  </td>
                  <td style={{ ...td, color: '#6b7280', whiteSpace: 'nowrap' }}>{r.durationMs} ms</td>
                  <td style={{ ...td, fontFamily: 'monospace', color: '#6b7280', whiteSpace: 'nowrap' }}
                      title={r.forwardedFor || ''}>
                    {r.ipAddress || '—'}
                  </td>
                  <td style={{ ...td, color: '#8a8f99', fontSize: '0.78rem', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={r.userAgent || ''}>
                    {r.userAgent || '—'}
                  </td>
                </tr>
              ))}
              {!loading && data.content.length === 0 && (
                <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: '#8a8f99', padding: '2rem' }}>
                  Aucun appel journalisé
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        <Pager page={page} totalPages={data.totalPages} totalElements={data.totalElements} onPage={setPage} />
    </Modal>
  );
}
